import { useState, useRef, useCallback, useEffect } from 'react'
import DashboardLayout from '../components/DashboardLayout'
import { T } from '../theme'

// Terrain horizontal — but gauche = notre but, but droit = adversaire
// Coordonnées en % : x=gauche→droite, y=haut→bas
const PITCH_W = 800
const PITCH_H = 520

// Formations : notre équipe attaque vers la droite
// GB à gauche, attaquants à droite
// [x%, y%] — x=0 gauche, x=100 droite, y=0 haut, y=100 bas
const FORMATIONS = {
  '4-3-3': { label: '4-3-3', positions: [
    [8, 50],                                          // GB
    [25,15],[25,38],[25,62],[25,85],                  // DEF
    [50,25],[50,50],[50,75],                          // MIL
    [75,15],[75,50],[75,85],                          // ATT
  ]},
  '4-4-2': { label: '4-4-2', positions: [
    [8, 50],
    [25,15],[25,38],[25,62],[25,85],
    [50,15],[50,38],[50,62],[50,85],
    [72,33],[72,67],
  ]},
  '4-2-3-1': { label: '4-2-3-1', positions: [
    [8, 50],
    [25,15],[25,38],[25,62],[25,85],
    [45,35],[45,65],
    [65,15],[65,50],[65,85],
    [80,50],
  ]},
  '3-5-2': { label: '3-5-2', positions: [
    [8, 50],
    [25,25],[25,50],[25,75],
    [48,10],[48,30],[48,50],[48,70],[48,90],
    [72,33],[72,67],
  ]},
  '3-4-3': { label: '3-4-3', positions: [
    [8, 50],
    [25,25],[25,50],[25,75],
    [50,15],[50,40],[50,60],[50,85],
    [75,15],[75,50],[75,85],
  ]},
  '5-3-2': { label: '5-3-2', positions: [
    [8, 50],
    [22,10],[22,30],[22,50],[22,70],[22,90],
    [50,25],[50,50],[50,75],
    [72,33],[72,67],
  ]},
}

const POS_ROLES = ['GB','DC','DG','DD','MDF','MC','MOC','AG','AD','BU','PTT']

const PLAYER_COLORS = {
  'GB':  '#f59e0b',
  'DC':  '#3b82f6', 'DG': '#3b82f6', 'DD': '#3b82f6',
  'MDF': '#4F8EF7', 'MC': '#4F8EF7', 'MOC': '#4F8EF7',
  'AG':  '#ef4444', 'AD': '#ef4444', 'BU': '#ef4444', 'PTT': '#ef4444',
}

function getPosColor(role) { return PLAYER_COLORS[role] || '#4F8EF7' }

const ARROW_TYPES = [
  { id: 'run',   label: 'Course',   color: '#f5f2eb', dash: '' },
  { id: 'pass',  label: 'Passe',    color: '#4F8EF7', dash: '8 4' },
  { id: 'press', label: 'Pressing', color: '#ef4444', dash: '4 4' },
]

const PHASES = {
  'possession': {
    label: 'Attaque placée',
    color: '#4F8EF7',
    desc: 'Construction depuis l\'arrière, largeur maximale',
    positions: [[8,50],[22,10],[22,35],[22,65],[22,90],[42,20],[42,50],[42,80],[65,15],[72,50],[65,85]],
  },
  'pressing': {
    label: 'Pressing haut',
    color: '#ef4444',
    desc: 'Récupération haute, triggers coordonnés',
    positions: [[15,50],[38,10],[38,35],[38,65],[38,90],[55,20],[55,50],[55,80],[72,20],[72,50],[72,80]],
  },
  'transition_def': {
    label: 'Transition DEF',
    color: '#3b82f6',
    desc: 'Repli rapide, bloc compact',
    positions: [[8,50],[20,18],[20,38],[20,62],[20,82],[35,25],[35,50],[35,75],[50,30],[50,50],[50,70]],
  },
  'bloc_median': {
    label: 'Bloc médian',
    color: '#f59e0b',
    desc: 'Deux lignes compactes, milieu de terrain',
    positions: [[8,50],[22,15],[22,38],[22,62],[22,85],[40,15],[40,38],[40,62],[40,85],[55,25],[55,75]],
  },
  'bloc_bas': {
    label: 'Bloc bas',
    color: '#22c55e',
    desc: 'Défense profonde, deux blocs devant le but',
    positions: [[8,50],[18,12],[18,33],[18,57],[18,78],[30,15],[30,38],[30,62],[30,85],[42,30],[42,70]],
  },
  'transition_off': {
    label: 'Transition OFF',
    color: '#a855f7',
    desc: 'Contre-attaque rapide, profondeur immédiate',
    positions: [[15,50],[28,20],[28,40],[28,60],[28,80],[50,35],[50,65],[45,50],[75,15],[80,50],[75,85]],
  },
  'cpa': {
    label: 'Corner offensif',
    color: '#f97316',
    desc: 'Organisation sur corner — attaque',
    positions: [[15,50],[25,40],[25,60],[30,75],[30,25],[70,25],[72,38],[72,62],[70,75],[65,50],[58,50]],
  },
}

function animatePlayers(fromPlayers, toPositions, setPlayers, duration = 550) {
  const start = performance.now()
  const from = fromPlayers.map(p => ({ x: p.x, y: p.y }))
  function step(now) {
    const t = Math.min((now - start) / duration, 1)
    const ease = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2, 3)/2
    setPlayers(prev => prev.map((p, i) => {
      const target = toPositions[i]
      if (!target) return p
      return { ...p, x: from[i].x + (target.x - from[i].x) * ease, y: from[i].y + (target.y - from[i].y) * ease }
    }))
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

function initPlayers(formation) {
  const pos = FORMATIONS[formation]?.positions || FORMATIONS['4-3-3'].positions
  const roles = ['GB','DD','DC','DC','DG','MC','MDF','MC','AD','BU','AG']
  return pos.map((p, i) => ({
    id: i,
    x: (p[0] / 100) * PITCH_W,
    y: (p[1] / 100) * PITCH_H,
    number: i === 0 ? 1 : i + 1,
    name: '',
    role: roles[i] || 'MC',
  }))
}

function PitchSVG() {
  const W = PITCH_W, H = PITCH_H
  const s = T.pitchLineStrong
  const sw = 1.2
  // Proportions FFF exactes sur 105m × 68m
  const mx = 28, my = 20
  const iW = W - mx*2, iH = H - my*2
  // Surface répa : 16.5m profondeur, 40.32m largeur
  const boxW = iW * (16.5/105)
  const boxH = iH * (40.32/68)
  // Petite surface : 5.5m profondeur, 18.32m largeur
  const sboxW = iW * (5.5/105)
  const sboxH = iH * (18.32/68)
  // But : 7.32m largeur
  const goalH = iH * (7.32/68)
  const goalD = 12
  // Point de penalty : 11m du but = 11/105 de iW
  const penX_L = mx + iW * (11/105)
  const penX_R = W - mx - iW * (11/105)
  // Rayon arc = 9.15m → en px
  const arcR = iH * (9.15/68)
  // L'arc coupe le bord de la surface — calculer y d'intersection
  // dist horizontale entre point penalty et bord surface
  const distL = penX_L - (mx + boxW)  // négatif si penalty avant surface
  const distR = (W - mx - boxW) - penX_R
  // chord y = sqrt(r² - dx²)
  const arcDy_L = Math.sqrt(Math.max(0, arcR*arcR - distL*distL))
  const arcDy_R = Math.sqrt(Math.max(0, arcR*arcR - distR*distR))

  return (
    <g>
      {/* Pelouse bandes verticales */}
      {Array.from({length: 10}).map((_, i) => (
        <rect key={i} x={i*(W/10)} y={0} width={W/10} height={H}
          fill={i%2===0 ? T.pitch : T.pitchBand} />
      ))}
      {/* Contour */}
      <rect x={mx} y={my} width={iW} height={iH} fill="none" stroke={s} strokeWidth={sw} />
      {/* Ligne médiane */}
      <line x1={W/2} y1={my} x2={W/2} y2={H-my} stroke={s} strokeWidth={sw} />
      {/* Rond central */}
      <circle cx={W/2} cy={H/2} r={iH*0.14} fill="none" stroke={s} strokeWidth={sw} />
      <circle cx={W/2} cy={H/2} r={3} fill={s} />
      {/* Point central */}
      <circle cx={W/2} cy={H/2} r={3} fill={s} />

      {/* Surface réparation gauche */}
      <rect x={mx} y={H/2 - boxH/2} width={boxW} height={boxH} fill="none" stroke={s} strokeWidth={sw} />
      {/* Petite surface gauche */}
      <rect x={mx} y={H/2 - sboxH/2} width={sboxW} height={sboxH} fill="none" stroke={s} strokeWidth={sw} />
      {/* But gauche */}
      <rect x={mx - goalD} y={H/2 - goalH/2} width={goalD} height={goalH} fill="none" stroke={s} strokeWidth={sw} />
      {/* Point de penalty gauche */}
      <circle cx={penX_L} cy={H/2} r={3} fill={s} />
      {/* Arc de réparation gauche — centré sur le point de penalty */}
      <path d={`M ${mx+boxW} ${H/2 - arcDy_L} A ${arcR} ${arcR} 0 0 1 ${mx+boxW} ${H/2 + arcDy_L}`}
        fill="none" stroke={s} strokeWidth={sw} />

      {/* Surface réparation droite */}
      <rect x={W-mx-boxW} y={H/2 - boxH/2} width={boxW} height={boxH} fill="none" stroke={s} strokeWidth={sw} />
      {/* Petite surface droite */}
      <rect x={W-mx-sboxW} y={H/2 - sboxH/2} width={sboxW} height={sboxH} fill="none" stroke={s} strokeWidth={sw} />
      {/* But droit */}
      <rect x={W-mx} y={H/2 - goalH/2} width={goalD} height={goalH} fill="none" stroke={s} strokeWidth={sw} />
      {/* Point de penalty droit */}
      <circle cx={penX_R} cy={H/2} r={3} fill={s} />
      {/* Arc de réparation droit — centré sur le point de penalty */}
      <path d={`M ${W-mx-boxW} ${H/2 - arcDy_R} A ${arcR} ${arcR} 0 0 0 ${W-mx-boxW} ${H/2 + arcDy_R}`}
        fill="none" stroke={s} strokeWidth={sw} />

      {/* Coins */}
      <path d={`M ${mx+10} ${my} A 10 10 0 0 0 ${mx} ${my+10}`} fill="none" stroke={s} strokeWidth={sw} />
      <path d={`M ${W-mx-10} ${my} A 10 10 0 0 1 ${W-mx} ${my+10}`} fill="none" stroke={s} strokeWidth={sw} />
      <path d={`M ${mx+10} ${H-my} A 10 10 0 0 1 ${mx} ${H-my-10}`} fill="none" stroke={s} strokeWidth={sw} />
      <path d={`M ${W-mx-10} ${H-my} A 10 10 0 0 0 ${W-mx} ${H-my-10}`} fill="none" stroke={s} strokeWidth={sw} />
    </g>
  )
}

function Arrow({ arrow, arrowType, onDelete, isSelected, onClick }) {
  const type = ARROW_TYPES.find(t => t.id === arrowType) || ARROW_TYPES[0]
  const dx = arrow.x2 - arrow.x1
  const dy = arrow.y2 - arrow.y1
  const len = Math.sqrt(dx*dx + dy*dy)
  if (len < 5) return null
  const ux = dx/len, uy = dy/len
  const arrowSize = 10
  const px = arrow.x2 - ux*arrowSize
  const py = arrow.y2 - uy*arrowSize
  const px1 = px - uy*5, py1 = py + ux*5
  const px2 = px + uy*5, py2 = py - ux*5

  return (
    <g onClick={e => { e.stopPropagation(); onClick(arrow.id) }} style={{ cursor: 'pointer' }}>
      <line x1={arrow.x1} y1={arrow.y1} x2={px} y2={py}
        stroke={type.color} strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={type.dash} opacity={0.85} />
      <polygon points={`${arrow.x2},${arrow.y2} ${px1},${py1} ${px2},${py2}`}
        fill={type.color} opacity={0.85} />
      {isSelected && (
        <text x={(arrow.x1+arrow.x2)/2} y={(arrow.y1+arrow.y2)/2 - 8}
          textAnchor="middle" fontSize="10" fill="#ef4444"
          fontFamily="monospace" style={{ cursor: 'pointer' }}
          onClick={e => { e.stopPropagation(); onDelete(arrow.id) }}>
          ✕
        </text>
      )}
    </g>
  )
}

function PlayerToken({ player, isSelected, isDragging, onMouseDown, onTouchStart, onClick, onNameChange, onRoleChange }) {
  const color = getPosColor(player.role)
  return (
    <g
      transform={`translate(${player.x}, ${player.y})`}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onClick={e => { e.stopPropagation(); onClick(player.id) }}
    >
      {/* Ombre */}
      <circle cx={2} cy={2} r={20} fill="rgba(0,0,0,0.35)" />
      {/* Cercle principal */}
      <circle cx={0} cy={0} r={20}
        fill={isSelected ? color : '#1a1916'}
        stroke={color}
        strokeWidth={isSelected ? 3 : 2}
      />
      {/* Numéro */}
      <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
        fontSize="12" fontWeight="800"
        fontFamily="'Anton', sans-serif"
        fill={isSelected ? '#1a1916' : color}
        style={{ pointerEvents: 'none' }}>
        {player.number}
      </text>
      {/* Nom */}
      <text x={0} y={32} textAnchor="middle" dominantBaseline="middle"
        fontSize="9" fontFamily="'JetBrains Mono', monospace"
        fill="rgba(245,242,235,0.85)"
        style={{ pointerEvents: 'none' }}>
        {player.name || player.role}
      </text>
    </g>
  )
}


function OpponentToken({ opponent, isDragging, onMouseDown, onTouchStart, onRemove }) {
  return (
    <g
      transform={`translate(${opponent.x}, ${opponent.y})`}
      style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      {/* Ombre */}
      <circle cx={2} cy={2} r={20} fill="rgba(0,0,0,0.25)" />
      {/* Cercle principal — blanc, contour gris plein */}
      <circle cx={0} cy={0} r={20}
        fill="#f0f0f0"
        stroke="#888"
        strokeWidth={2.5}
      />
      {/* Croix supprimer — coin haut droit */}
      <g onClick={e => { e.stopPropagation(); onRemove(opponent.id) }}
         style={{ cursor: 'pointer' }}>
        <circle cx={14} cy={-14} r={7} fill="rgba(80,80,80,0.85)" />
        <text x={14} y={-14} textAnchor="middle" dominantBaseline="middle"
          fontSize="9" fill="#f5f2eb" fontFamily="monospace"
          style={{ pointerEvents: 'none' }}>✕</text>
      </g>
      {/* Numéro */}
      <text x={0} y={1} textAnchor="middle" dominantBaseline="middle"
        fontSize="12" fontWeight="800"
        fontFamily="'Anton', sans-serif"
        fill="#1a1916"
        style={{ pointerEvents: 'none' }}>
        {opponent.number}
      </text>
      {/* Label ADV */}
      <text x={0} y={32} textAnchor="middle" dominantBaseline="middle"
        fontSize="8" fontFamily="'JetBrains Mono', monospace"
        fill="rgba(80,80,80,0.9)"
        style={{ pointerEvents: 'none' }}>
        ADV
      </text>
    </g>
  )
}

// Police Anton convertie en @font-face base64, fetchee une seule fois
// puis mise en cache module-level. Sans ca, le SVG rasterise via <img>
// pour l'export PNG n'a pas Anton (texte en police fallback) ; et un
// @font-face a URL externe dans le SVG tainte le canvas -> toDataURL
// leve SecurityError. fonts.gstatic.com sert les .woff2 en CORS (ACAO *)
// donc le fetch + arrayBuffer est autorise cross-origin.
let _antonFontFacePromise = null

async function getAntonFontFaceCss() {
  if (_antonFontFacePromise) return _antonFontFacePromise
  _antonFontFacePromise = (async () => {
    const cssResp = await fetch('https://fonts.googleapis.com/css2?family=Anton')
    if (!cssResp.ok) throw new Error('Anton CSS fetch failed')
    const css = await cssResp.text()
    const m = css.match(/url\((https:\/\/fonts\.gstatic\.com\/[^)]+\.woff2)\)/)
    if (!m) throw new Error('Anton woff2 URL introuvable dans le CSS')
    const fontResp = await fetch(m[1])
    if (!fontResp.ok) throw new Error('Anton woff2 fetch failed')
    const buf = await fontResp.arrayBuffer()
    const bytes = new Uint8Array(buf)
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    const b64 = btoa(bin)
    return `@font-face{font-family:'Anton';font-style:normal;font-weight:400;src:url(data:font/woff2;base64,${b64}) format('woff2');}`
  })().catch(err => {
    // Reset le cache pour qu'un prochain export retente le fetch.
    _antonFontFacePromise = null
    throw err
  })
  return _antonFontFacePromise
}

export default function Insightboard() {
  const [formation, setFormation] = useState('4-3-3')
  const [players, setPlayers] = useState(() => initPlayers('4-3-3'))
  const [arrows, setArrows] = useState([])
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [selectedArrow, setSelectedArrow] = useState(null)
  const [arrowType, setArrowType] = useState('run')
  const [mode, setMode] = useState('select') // 'select' | 'arrow'
  const [dragging, setDragging] = useState(null)
  const [arrowStart, setArrowStart] = useState(null)
  const [previewArrow, setPreviewArrow] = useState(null)
  const [editingPlayer, setEditingPlayer] = useState(null)
  const [activePhase, setActivePhase] = useState(null)
  const [activeTab, setActiveTab] = useState('formation')
  const [ball, setBall] = useState({ x: PITCH_W / 2, y: PITCH_H / 2 })
  const [draggingBall, setDraggingBall] = useState(false)
  const [opponents, setOpponents] = useState([])
  const [draggingOpponent, setDraggingOpponent] = useState(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [exportError, setExportError] = useState(false)
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  // Refs pour les handlers touch DOM (évite de recréer les listeners à chaque render)
  const handleSVGMouseMoveRef = useRef(null)
  const handleSVGMouseUpRef = useRef(null)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const getSVGPoint = useCallback((e) => {
    const svg = svgRef.current
    if (!svg) return { x: 0, y: 0 }
    const rect = svg.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    const scaleX = PITCH_W / rect.width
    const scaleY = PITCH_H / rect.height
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }, [])

  const handleFormationChange = (f) => {
    setFormation(f)
    setPlayers(initPlayers(f))
    setArrows([])
    setSelectedPlayer(null)
    setSelectedArrow(null)
  }

  const handlePlayerMouseDown = useCallback((e, playerId) => {
    e.preventDefault()
    e.stopPropagation()
    if (mode === 'arrow') {
      const p = players.find(p => p.id === playerId)
      setArrowStart({ x: p.x, y: p.y, playerId })
      return
    }
    setDragging(playerId)
    setSelectedPlayer(playerId)
    setSelectedArrow(null)
  }, [mode, players])

  const handleSVGMouseMove = useCallback((e) => {
    const pt = getSVGPoint(e)
    if (dragging !== null) {
      setPlayers(prev => prev.map(p =>
        p.id === dragging
          ? { ...p, x: Math.max(22, Math.min(PITCH_W-22, pt.x)), y: Math.max(18, Math.min(PITCH_H-18, pt.y)) }
          : p
      ))
    }
    if (draggingBall) {
      setBall({ x: Math.max(22, Math.min(PITCH_W-22, pt.x)), y: Math.max(18, Math.min(PITCH_H-18, pt.y)) })
    }
    if (draggingOpponent !== null) {
      setOpponents(prev => prev.map(o =>
        o.id === draggingOpponent
          ? { ...o, x: Math.max(22, Math.min(PITCH_W-22, pt.x)), y: Math.max(18, Math.min(PITCH_H-18, pt.y)) }
          : o
      ))
    }
    if (arrowStart) {
      setPreviewArrow({ x1: arrowStart.x, y1: arrowStart.y, x2: pt.x, y2: pt.y })
    }
  }, [dragging, draggingBall, draggingOpponent, arrowStart, getSVGPoint])

  const handleSVGMouseUp = useCallback((e) => {
    if (arrowStart && previewArrow) {
      const endPt = getSVGPoint(e)
      const dist = Math.sqrt((endPt.x - arrowStart.x)**2 + (endPt.y - arrowStart.y)**2)
      if (dist > 20) {
        setArrows(prev => [...prev, {
          id: Date.now(),
          x1: arrowStart.x, y1: arrowStart.y,
          x2: endPt.x, y2: endPt.y,
          type: arrowType,
        }])
      }
      setArrowStart(null)
      setPreviewArrow(null)
    }
    setDragging(null)
    setDraggingBall(false)
    setDraggingOpponent(null)
  }, [arrowStart, previewArrow, arrowType, getSVGPoint])

  const handleSVGClick = useCallback(() => {
    setSelectedPlayer(null)
    setSelectedArrow(null)
    setEditingPlayer(null)
  }, [])

  // Mettre à jour les refs à chaque render pour que les listeners DOM aient toujours la version à jour
  useEffect(() => {
    handleSVGMouseMoveRef.current = handleSVGMouseMove
    handleSVGMouseUpRef.current = handleSVGMouseUp
  })

  // Attacher touchmove + touchend directement sur le DOM avec passive:false
  // pour pouvoir appeler preventDefault() et bloquer le scroll pendant le drag
  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const onTouchMove = (e) => {
      if (handleSVGMouseMoveRef.current) {
        e.preventDefault()
        handleSVGMouseMoveRef.current(e)
      }
    }
    const onTouchEnd = (e) => {
      if (handleSVGMouseUpRef.current) {
        handleSVGMouseUpRef.current(e)
      }
    }
    svg.addEventListener('touchmove', onTouchMove, { passive: false })
    svg.addEventListener('touchend', onTouchEnd, { passive: false })
    return () => {
      svg.removeEventListener('touchmove', onTouchMove)
      svg.removeEventListener('touchend', onTouchEnd)
    }
  }, []) // une seule fois au montage — les refs se mettent à jour côté dessus

  const handlePlayerClick = useCallback((playerId) => {
    if (mode === 'select') {
      setSelectedPlayer(playerId)
      setSelectedArrow(null)
    }
  }, [mode])

  const applyPhase = (phaseKey) => {
    const phase = PHASES[phaseKey]
    if (!phase) return
    setActivePhase(phaseKey)
    setArrows([])
    setSelectedPlayer(null)
    const targets = phase.positions.map(p => ({
      x: (p[0] / 100) * PITCH_W,
      y: (p[1] / 100) * PITCH_H,
    }))
    animatePlayers(players, targets, setPlayers)
  }

  const handlePlayerDblClick = useCallback((playerId) => {
    setEditingPlayer(playerId)
    setSelectedPlayer(playerId)
  }, [])

  const addOpponent = () => {
    if (opponents.length >= 11) return
    const nextNum = Array.from({length: 11}, (_, i) => i + 1).find(n => !opponents.find(o => o.number === n)) || (opponents.length + 1)
    setOpponents(prev => [...prev, {
      id: Date.now(),
      number: nextNum,
      x: PITCH_W * 0.75 + (Math.random() - 0.5) * 80,
      y: PITCH_H * 0.5 + (Math.random() - 0.5) * 120,
    }])
  }

  const removeOpponent = (id) => {
    setOpponents(prev => prev.filter(o => o.id !== id))
  }

  const deleteSelected = () => {
    if (selectedArrow !== null) {
      setArrows(prev => prev.filter(a => a.id !== selectedArrow))
      setSelectedArrow(null)
    }
  }

  const exportPNG = async () => {
    const svg = svgRef.current
    if (!svg) return
    try {
      // Clone pour ne pas muter le SVG affiche a l'ecran.
      const clone = svg.cloneNode(true)

      // Injecter Anton en base64 dans un <style> du <defs>. Si le fetch
      // de la police echoue (reseau), on continue quand meme : le texte
      // sortira en police fallback plutot que de bloquer tout l'export.
      try {
        const fontFaceCss = await getAntonFontFaceCss()
        let defs = clone.querySelector('defs')
        if (!defs) {
          defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')
          clone.insertBefore(defs, clone.firstChild)
        }
        const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
        style.textContent = fontFaceCss
        defs.appendChild(style)
      } catch (fontErr) {
        console.warn('Anton font embed failed, export sans police custom:', fontErr)
      }

      const serializer = new XMLSerializer()
      let svgStr = serializer.serializeToString(clone)
      if (!svgStr.includes('xmlns=')) {
        svgStr = svgStr.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
      }
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)

      await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = PITCH_W * 2
            canvas.height = PITCH_H * 2
            const ctx = canvas.getContext('2d')
            ctx.scale(2, 2)
            ctx.drawImage(img, 0, 0)
            const dataUrl = canvas.toDataURL('image/png')
            // Attacher le lien au DOM avant click (requis Safari + certains Chrome)
            const link = document.createElement('a')
            link.download = `insightboard-${formation}-${Date.now()}.png`
            link.href = dataUrl
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
            resolve()
          } catch (e) {
            URL.revokeObjectURL(url)
            reject(e)
          }
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          reject(new Error('Chargement de l\'image SVG echoue'))
        }
        img.src = url
      })
    } catch (e) {
      console.error('Export PNG echoue:', e)
      setExportError(true)
      setTimeout(() => setExportError(false), 3000)
    }
  }

  const selectedPlayerData = players.find(p => p.id === selectedPlayer)

  return (
    <DashboardLayout>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .tp-btn { transition: all .15s; }
        .tp-btn:hover { opacity: .85; }
      `}</style>

      {/* Toast erreur export — feedback utilisateur visible (avant : juste
          un console.error muet) */}
      {exportError && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          zIndex: 4000,
          background: T.dark, color: '#f5f2eb',
          border: '1px solid #ef4444',
          padding: '12px 22px',
          fontFamily: T.mono, fontSize: 11, letterSpacing: '.06em',
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
        }}>
          Export impossible, réessaie.
        </div>
      )}

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 16, marginBottom: 24, paddingBottom: 20, borderBottom: `1px solid ${T.rule}` }}>
        <div>
          <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ width: 16, height: 1, background: T.gold, display: 'inline-block' }} />Insightboard
          </div>
          <h1 style={{ fontFamily: T.display, fontSize: isMobile ? 28 : 48, textTransform: 'uppercase', lineHeight: .88, letterSpacing: '.01em', margin: 0 }}>
            <span style={{ color: T.ink }}>Insight</span><span style={{ color: T.gold }}>board.</span>
          </h1>
        </div>
        <button onClick={exportPNG} className="tp-btn" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 20px', background: T.dark, color: '#f5f2eb',
          fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
          border: 'none', cursor: 'pointer', alignSelf: isMobile ? 'stretch' : 'flex-start',
          justifyContent: 'center', marginTop: 8,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Exporter PNG
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 240px', gap: 16, animation: 'fadeIn .35s ease' }}>

        {/* ── TERRAIN ── */}
        <div ref={containerRef}>
          {/* Onglets Formation / Phases */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: `2px solid ${T.rule}` }}>
            {[{ id: 'formation', label: 'Formation' }, { id: 'phases', label: 'Phases de jeu' }].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '9px 20px',
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                background: 'transparent', border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${T.gold}` : '2px solid transparent',
                marginBottom: -2,
                color: activeTab === tab.id ? T.gold : T.muted,
                cursor: 'pointer', transition: 'all .15s',
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Toolbar Formation */}
          {activeTab === 'formation' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {Object.entries(FORMATIONS).map(([key, f]) => (
              <button key={key} onClick={() => handleFormationChange(key)} className="tp-btn" style={{
                padding: '7px 14px',
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                background: formation === key ? T.gold : T.surface,
                color: formation === key ? T.dark : T.muted,
                border: `1px solid ${formation === key ? T.gold : T.rule}`,
                cursor: 'pointer', fontWeight: formation === key ? 700 : 400,
              }}>
                {f.label}
              </button>
            ))}
            <div style={{ width: 1, height: 24, background: T.rule, margin: '0 4px' }} />
            {[{ id: 'select', icon: '↖', label: 'Déplacer' }, { id: 'arrow', icon: '→', label: 'Flèche' }].map(m => (
              <button key={m.id} onClick={() => setMode(m.id)} className="tp-btn" style={{
                padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                background: mode === m.id ? T.dark : T.surface,
                color: mode === m.id ? '#f5f2eb' : T.muted,
                border: `1px solid ${mode === m.id ? T.dark : T.rule}`,
                cursor: 'pointer',
              }}>
                <span style={{ fontSize: 12 }}>{m.icon}</span>{m.label}
              </button>
            ))}
            {mode === 'arrow' && ARROW_TYPES.map(at => (
              <button key={at.id} onClick={() => setArrowType(at.id)} className="tp-btn" style={{
                padding: '6px 12px',
                fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase',
                background: arrowType === at.id ? at.color + '18' : T.surface,
                color: arrowType === at.id ? at.color : T.muted,
                border: `1px solid ${arrowType === at.id ? at.color : T.rule}`,
                cursor: 'pointer',
              }}>
                {at.label}
              </button>
            ))}
            {selectedArrow !== null && (
              <button onClick={deleteSelected} className="tp-btn" style={{
                padding: '6px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                color: '#ef4444', fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em',
                textTransform: 'uppercase', cursor: 'pointer',
              }}>
                Supprimer flèche
              </button>
            )}
          </div>
          )}

          {/* Toolbar Phases de jeu */}
          {activeTab === 'phases' && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {Object.entries(PHASES).map(([key, phase]) => (
              <button key={key} onClick={() => applyPhase(key)} className="tp-btn" style={{
                padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 7,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                background: activePhase === key ? phase.color + '20' : T.surface,
                color: activePhase === key ? phase.color : T.muted,
                border: `1px solid ${activePhase === key ? phase.color : T.rule}`,
                cursor: 'pointer',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color, flexShrink: 0 }} />
                {phase.label}
              </button>
            ))}
            {activePhase && (
              <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.04em', fontStyle: 'italic', marginLeft: 4 }}>
                {PHASES[activePhase]?.desc}
              </span>
            )}
          </div>
          )}

          {/* SVG Terrain */}
          <div style={{ border: `2px solid ${T.rule}`, overflow: 'hidden', borderRadius: 2 }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${PITCH_W} ${PITCH_H}`}
              style={{ display: 'block', width: '100%', cursor: mode === 'arrow' ? 'crosshair' : 'default' }}
              onMouseMove={handleSVGMouseMove}
              onMouseUp={handleSVGMouseUp}
              onMouseLeave={handleSVGMouseUp}
              onClick={handleSVGClick}
            >
              <PitchSVG />

              {/* Flèches */}
              {arrows.map(arrow => (
                <Arrow
                  key={arrow.id}
                  arrow={arrow}
                  arrowType={arrow.type}
                  isSelected={selectedArrow === arrow.id}
                  onClick={(id) => { setSelectedArrow(id); setSelectedPlayer(null) }}
                  onDelete={(id) => setArrows(prev => prev.filter(a => a.id !== id))}
                />
              ))}

              {/* Preview flèche */}
              {previewArrow && (
                <line
                  x1={previewArrow.x1} y1={previewArrow.y1}
                  x2={previewArrow.x2} y2={previewArrow.y2}
                  stroke="rgba(245,242,235,0.5)" strokeWidth={1.5}
                  strokeDasharray="6 3"
                />
              )}

              {/* Joueurs */}
              {players.map(player => (
                <g key={player.id}
                  onDoubleClick={() => handlePlayerDblClick(player.id)}
                >
                  <PlayerToken
                    player={player}
                    isSelected={selectedPlayer === player.id}
                    isDragging={dragging === player.id}
                    onMouseDown={(e) => handlePlayerMouseDown(e, player.id)}
                    onClick={handlePlayerClick}
                    onTouchStart={(e) => { e.preventDefault(); handlePlayerMouseDown(e, player.id) }}
                  />
                </g>
              ))}

              {/* Adversaires */}
              {opponents.map(opp => (
                <g key={opp.id} onDoubleClick={e => e.stopPropagation()}>
                  <OpponentToken
                    opponent={opp}
                    isDragging={draggingOpponent === opp.id}
                    onRemove={removeOpponent}
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setDraggingOpponent(opp.id); setSelectedPlayer(null) }}
                    onTouchStart={e => { e.preventDefault(); setDraggingOpponent(opp.id); setSelectedPlayer(null) }}
                  />
                </g>
              ))}

              {/* Ballon */}
              <g
                transform={`translate(${ball.x}, ${ball.y})`}
                style={{ cursor: draggingBall ? 'grabbing' : 'grab', userSelect: 'none' }}
                onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setDraggingBall(true) }}
                onTouchStart={e => { e.preventDefault(); setDraggingBall(true) }}
              >
                {/* Ombre portée */}
                <ellipse cx={3} cy={4} rx={12} ry={7} fill="rgba(0,0,0,0.35)" />
                {/* Corps — cuir blanc cassé */}
                <circle cx={0} cy={0} r={13} fill="#f2efe8" stroke="#1a1916" strokeWidth={1.2} />
                {/* Panneau central hexagone */}
                <polygon points="0,-6 5.2,-3 5.2,3 0,6 -5.2,3 -5.2,-3" fill="#1a1916" opacity={0.85} />
                {/* Panneaux périphériques */}
                <polygon points="0,-13 4,-9 0,-6 -4,-9" fill="#1a1916" opacity={0.55} />
                <polygon points="11.3,-6.5 11,0 5.2,-3 5.2,-8" fill="#1a1916" opacity={0.45} />
                <polygon points="11.3,6.5 5.2,8 5.2,3 11,0" fill="#1a1916" opacity={0.45} />
                <polygon points="0,13 -4,9 0,6 4,9" fill="#1a1916" opacity={0.55} />
                <polygon points="-11.3,6.5 -11,0 -5.2,3 -5.2,8" fill="#1a1916" opacity={0.45} />
                <polygon points="-11.3,-6.5 -5.2,-8 -5.2,-3 -11,0" fill="#1a1916" opacity={0.45} />
                {/* Reflet */}
                <ellipse cx={-4} cy={-5} rx={4} ry={2.5} fill="rgba(255,255,255,0.45)" transform="rotate(-30 -4 -5)" />
              </g>
            </svg>
          </div>

          {/* Légende */}
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            {ARROW_TYPES.map(at => (
              <div key={at.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="24" height="8" viewBox="0 0 24 8">
                  <line x1="0" y1="4" x2="20" y2="4" stroke={at.color} strokeWidth="2" strokeDasharray={at.dash} />
                  <polygon points="24,4 18,1 18,7" fill={at.color} />
                </svg>
                <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.06em' }}>{at.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── PANNEAU DROITE ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Éditeur joueur sélectionné */}
          {selectedPlayerData ? (
            <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid ${T.gold}`, padding: '20px 18px' }}>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 12, height: 1, background: T.gold }} />Joueur #{selectedPlayerData.number}
              </div>

              {/* Numéro */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, display: 'block', marginBottom: 6 }}>Numéro</label>
                <input
                  type="number" min="1" max="99"
                  value={selectedPlayerData.number}
                  onChange={e => setPlayers(prev => prev.map(p => p.id === selectedPlayer ? { ...p, number: parseInt(e.target.value) || p.number } : p))}
                  style={{ width: '100%', background: T.bg, border: `1px solid ${T.rule}`, padding: '8px 12px', color: T.ink, fontFamily: T.mono, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = T.gold}
                  onBlur={e => e.target.style.borderColor = T.rule}
                />
              </div>

              {/* Nom */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, display: 'block', marginBottom: 6 }}>Nom</label>
                <input
                  type="text" placeholder="Ferracci A."
                  value={selectedPlayerData.name}
                  onChange={e => setPlayers(prev => prev.map(p => p.id === selectedPlayer ? { ...p, name: e.target.value } : p))}
                  style={{ width: '100%', background: T.bg, border: `1px solid ${T.rule}`, padding: '8px 12px', color: T.ink, fontFamily: T.mono, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderColor = T.gold}
                  onBlur={e => e.target.style.borderColor = T.rule}
                />
              </div>

              {/* Rôle */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, display: 'block', marginBottom: 6 }}>Poste</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                  {POS_ROLES.map(role => (
                    <button key={role} onClick={() => setPlayers(prev => prev.map(p => p.id === selectedPlayer ? { ...p, role } : p))}
                      style={{
                        padding: '6px 4px', textAlign: 'center',
                        fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em',
                        background: selectedPlayerData.role === role ? getPosColor(role) + '18' : T.bg,
                        color: selectedPlayerData.role === role ? getPosColor(role) : T.muted,
                        border: `1px solid ${selectedPlayerData.role === role ? getPosColor(role) : T.rule}`,
                        cursor: 'pointer',
                      }}>
                      {role}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={() => setSelectedPlayer(null)} style={{
                width: '100%', padding: '9px', background: 'transparent',
                border: `1px solid ${T.rule}`, color: T.muted,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                cursor: 'pointer',
              }}>
                Fermer
              </button>
            </div>
          ) : (
            <div style={{ background: T.surface, border: `1px solid ${T.rule}`, padding: '20px 18px' }}>
              <div style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, marginBottom: 12 }}>Mode</div>
              <div style={{ fontFamily: T.mono, fontSize: 10, color: mode === 'arrow' ? T.gold : T.ink, letterSpacing: '.06em', marginBottom: 12 }}>
                {mode === 'select' ? 'Cliquer un joueur pour l\'éditer, glisser pour le déplacer.' : 'Cliquer un joueur puis glisser vers la destination pour tracer une flèche.'}
              </div>
              <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.06em', lineHeight: 1.7 }}>
                Double-clic sur un joueur pour éditer son nom et son numéro.
              </div>
            </div>
          )}

          {/* Liste joueurs */}
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid rgba(79,142,247,0.25)` }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 12, height: 1, background: T.gold }} />Formation {formation}
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {players.map((p, i) => {
                const color = getPosColor(p.role)
                return (
                  <div key={p.id}
                    onClick={() => { setSelectedPlayer(p.id); setSelectedArrow(null) }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 16px', borderBottom: `1px solid ${T.rule}`,
                      background: selectedPlayer === p.id ? T.goldBg : 'transparent',
                      cursor: 'pointer', transition: 'background .1s',
                    }}
                    onMouseEnter={e => { if (selectedPlayer !== p.id) e.currentTarget.style.background = T.bg }}
                    onMouseLeave={e => { if (selectedPlayer !== p.id) e.currentTarget.style.background = 'transparent' }}
                  >
                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: color + '15', border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: T.display, fontSize: 10, color }}>{p.number}</span>
                    </div>
                    <span style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, flex: 1 }}>{p.name || '—'}</span>
                    <span style={{ fontFamily: T.mono, fontSize: 8, color, letterSpacing: '.06em' }}>{p.role}</span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Adversaires */}
          <div style={{ background: T.surface, border: `1px solid ${T.rule}`, borderTop: `2px solid rgba(180,180,180,0.4)` }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: T.muted, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 12, height: 1, background: T.muted }} />Adversaires ({opponents.length}/11)
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={addOpponent} disabled={opponents.length >= 11}
                  style={{ width: 26, height: 26, border: `1px solid ${T.rule}`, background: T.bg, color: T.ink, fontFamily: T.mono, fontSize: 14, cursor: opponents.length >= 11 ? 'not-allowed' : 'pointer', opacity: opponents.length >= 11 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  +
                </button>
                <button onClick={() => setOpponents(prev => prev.slice(0, -1))} disabled={opponents.length === 0}
                  style={{ width: 26, height: 26, border: `1px solid ${T.rule}`, background: T.bg, color: T.muted, fontFamily: T.mono, fontSize: 14, cursor: opponents.length === 0 ? 'not-allowed' : 'pointer', opacity: opponents.length === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  -
                </button>
              </div>
            </div>
            {opponents.length === 0 && (
              <div style={{ padding: '12px 16px', fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.04em' }}>Appuie sur + pour ajouter des adversaires.</div>
            )}
            <div style={{ maxHeight: 140, overflowY: 'auto' }}>
              {opponents.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 16px', borderBottom: `1px solid ${T.rule}` }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0f0f0', border: '1.5px solid #888', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontFamily: T.display, fontSize: 9, color: '#1a1916' }}>{o.number}</span>
                  </div>
                  <span style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, flex: 1 }}>{o.number === 1 ? 'GB Adv.' : `Joueur ${o.number}`}</span>
                  <button onClick={() => removeOpponent(o.id)} style={{ background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 12, padding: '2px 4px' }}>✕</button>
                </div>
              ))}
            </div>
          </div>

          {/* Reset */}
          <button onClick={() => { setArrows([]); setSelectedPlayer(null); setSelectedArrow(null) }} style={{
            padding: '10px', background: 'transparent',
            border: `1px solid ${T.rule}`, color: T.muted,
            fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
            cursor: 'pointer',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = '#ef4444'}
            onMouseLeave={e => e.currentTarget.style.borderColor = T.rule}
          >
            Effacer les flèches
          </button>
        </div>
      </div>
    </DashboardLayout>
  )
}
